import React from "react";
import type { MeasureValue } from "../../../types/stationPerformance";
import { NOT_APPLICABLE_REASONS } from "../StationPerformance.consts";

interface MeasureValueCellProps {
  measure: MeasureValue | null | undefined;
  /** "money" renders cents as euro; "percent" appends %; "number" is plain. */
  format?: "number" | "money" | "percent";
  decimals?: number;
  suffix?: string;
}

/**
 * Renders a possibly-undefined measure.
 *
 * The whole reason this component exists: an undefined average must never render as `0`. A fully-free
 * station's money-per-paid-session is *unmeasurable*, and showing `0` would rank it as the worst
 * performer in the fleet rather than as not applicable on that axis. An em-dash plus the reason on
 * hover keeps the distinction visible without cluttering the table.
 */
const MeasureValueCell: React.FC<MeasureValueCellProps> = ({
  measure,
  format = "number",
  decimals = 2,
  suffix = "",
}) => {
  if (!measure || measure.status === "NOT_APPLICABLE" || measure.value === null) {
    const reason = measure?.reason ? NOT_APPLICABLE_REASONS[measure.reason] : "Not applicable";
    return (
      <span className="text-color-secondary" title={reason ?? "Not applicable"}>
        —
      </span>
    );
  }

  const value = measure.value;
  if (format === "money") {
    // Money arrives as integer cents; dividing here is the only place that happens.
    return <span>{(value / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" })}</span>;
  }
  if (format === "percent") {
    return <span>{value.toFixed(decimals)}%</span>;
  }
  return (
    <span>
      {value.toLocaleString(undefined, { maximumFractionDigits: decimals })}
      {suffix}
    </span>
  );
};

export default MeasureValueCell;
